import React, { useState, useEffect } from 'react';
import { Search, Download, CreditCard, CheckCircle, Clock, Printer, Filter } from 'lucide-react';
import { Link } from 'react-router-dom';
import api from '@/api/api';
import { toast } from 'sonner';
import { formatDate } from '../../utils/dateTimeUtils';

const StaffICardReport = () => {
  const [staffList, setStaffList] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [statistics, setStatistics] = useState({ total: 0, printed: 0, pending: 0 });
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState({
    department: '',
    staff_type: 'all',
    status: 'all'
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchReport();
  }, [filters.department, filters.staff_type, filters.status]);

  const fetchReport = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.department) params.append('department', filters.department);
      if (filters.staff_type && filters.staff_type !== 'all') params.append('staff_type', filters.staff_type);
      if (filters.status && filters.status !== 'all') params.append('status', filters.status);
      
      const response = await api.get(`/staff-icard/report?${params.toString()}`);
      const data = response.data;
      
      setStaffList(data.staff || []);
      setDepartments(data.departments || []);
      setStatistics(data.statistics || { total: 0, printed: 0, pending: 0 });
    } catch (error) {
      console.error('Error fetching report:', error);
      toast.error('Failed to load report');
    } finally {
      setLoading(false);
    }
  };

  const getStaffType = (staff) => {
    if (staff.staff_type) return staff.staff_type;
    const roles = staff.roles || [];
    if (roles.includes('Teacher') || roles.includes('HOD') || roles.includes('Principal')) {
      return 'Teaching';
    }
    return 'Non-Teaching';
  };

  const filteredStaff = staffList.filter(staff => {
    const matchesSearch = 
      (staff.full_name?.toLowerCase() || '').includes(searchTerm.toLowerCase()) ||
      (staff.employee_id?.toLowerCase() || '').includes(searchTerm.toLowerCase());
    return matchesSearch;
  });

  const exportToCSV = () => {
    if (filteredStaff.length === 0) {
      toast.error('No data available for export');
      return;
    }
    
    const headers = ['Employee ID', 'Staff Name', 'Staff Type', 'Department', 'Designation', 'I-Card Status', 'Print Count', 'Last Printed', 'Printed By'];
    const csvData = filteredStaff.map(staff => [
      staff.employee_id || '',
      staff.full_name || '',
      getStaffType(staff),
      staff.department || '',
      staff.designation || '',
      (staff.icard_print_count || 0) > 0 ? 'Printed' : 'Pending',
      staff.icard_print_count || 0,
      staff.icard_last_printed ? new Date(staff.icard_last_printed).toLocaleString('en-IN') : '',
      staff.icard_last_printed_by || ''
    ]);
    
    const csvContent = [
      headers.join(','),
      ...csvData.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `staff_icard_report_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    toast.success('CSV exported successfully');
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Staff I-Card Report</h1>
          <p className="text-sm text-gray-500 mt-1">View Staff I-Card print status and reports</p>
        </div>
        <div className="flex gap-3">
          <Link to="/hr/print-staff-icard" className="flex items-center gap-2 bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700">
            <Printer className="h-4 w-4" />
            Print Staff I-Card
          </Link>
          <button onClick={exportToCSV} disabled={filteredStaff.length === 0} className="flex items-center gap-2 bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 disabled:bg-gray-300">
            <Download className="h-4 w-4" />
            Export CSV
          </button>
        </div>
      </div>

      {/* Statistics */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white p-6 rounded-lg shadow">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Total Staff</p>
              <p className="text-3xl font-bold text-gray-900 mt-1">{statistics.total}</p>
            </div>
            <CreditCard className="h-12 w-12 text-purple-500" />
          </div>
        </div>
        <div className="bg-white p-6 rounded-lg shadow">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">I-Card Printed</p>
              <p className="text-3xl font-bold text-green-600 mt-1">{statistics.printed}</p>
            </div>
            <CheckCircle className="h-12 w-12 text-green-500" />
          </div>
        </div>
        <div className="bg-white p-6 rounded-lg shadow">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Pending</p>
              <p className="text-3xl font-bold text-orange-600 mt-1">{statistics.pending}</p>
            </div>
            <Clock className="h-12 w-12 text-orange-500" />
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow p-4">
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
          <div className="relative md:col-span-2">
            <Search className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search by name or Employee ID..."
              className="pl-10 pr-4 py-2 w-full border rounded-lg focus:ring-2 focus:ring-purple-500"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <select
            className="px-3 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500"
            value={filters.department}
            onChange={(e) => setFilters({ ...filters, department: e.target.value })}
          >
            <option value="">All Departments</option>
            {departments.map(dept => (<option key={dept} value={dept}>{dept}</option>))}
          </select>
          <select
            className="px-3 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500"
            value={filters.staff_type}
            onChange={(e) => setFilters({ ...filters, staff_type: e.target.value })}
          >
            <option value="all">All Staff Types</option>
            <option value="Teaching">Teaching</option>
            <option value="Non-Teaching">Non-Teaching</option>
          </select>
          <select
            className="px-3 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500"
            value={filters.status}
            onChange={(e) => setFilters({ ...filters, status: e.target.value })}
          >
            <option value="all">All Status</option>
            <option value="printed">Printed</option>
            <option value="pending">Pending</option>
          </select>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-lg shadow overflow-x-auto">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Staff</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Employee ID</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Staff Type</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Department</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Designation</th>
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase">I-Card Status</th>
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase">Print Count</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Last Printed</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {loading ? (
                <tr>
                  <td colSpan="8" className="px-6 py-8 text-center">
                    <div className="flex justify-center"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div></div>
                  </td>
                </tr>
              ) : filteredStaff.length === 0 ? (
                <tr>
                  <td colSpan="8" className="px-6 py-8 text-center text-gray-500">No staff found</td>
                </tr>
              ) : (
                filteredStaff.map((staff) => (
                  <tr key={staff.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-gray-200 overflow-hidden">
                          {staff.photo ? (
                            <img src={staff.photo} className="w-full h-full object-cover" alt="" />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center text-gray-400 text-xs">N/A</div>
                          )}
                        </div>
                        <span className="font-medium text-gray-900">{staff.full_name}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-600">{staff.employee_id}</td>
                    <td className="px-6 py-4">
                      <span className={`px-2 py-1 text-xs rounded-full font-medium ${getStaffType(staff) === 'Teaching' ? 'bg-blue-100 text-blue-700' : 'bg-green-100 text-green-700'}`}>
                        {getStaffType(staff)}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-600">{staff.department || '-'}</td>
                    <td className="px-6 py-4 text-sm text-gray-600">{staff.designation || '-'}</td>
                    <td className="px-6 py-4 text-center">
                      {(staff.icard_print_count || 0) > 0 ? (
                        <span className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800">
                          <CheckCircle className="h-3 w-3" />Printed
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-semibold rounded-full bg-orange-100 text-orange-800">
                          <Clock className="h-3 w-3" />Pending
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4 text-center font-semibold text-gray-900">{staff.icard_print_count || 0}</td>
                    <td className="px-6 py-4 text-sm text-gray-600">
                      {staff.icard_last_printed ? (
                        <div>
                          <p>{formatDate(staff.icard_last_printed)}</p>
                          <p className="text-xs text-gray-400">{staff.icard_last_printed_by}</p>
                        </div>
                      ) : '-'}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default StaffICardReport;
